import {
  getAdminContractDisplayStatusLabel as getAdminContractDisplayStatusLabelBase,
  getAdminContractDisplayStatusMeta as getAdminContractDisplayStatusMetaBase,
  getAdminContractStatusLabel as getAdminContractStatusLabelBase,
  getArtistContractStatusLabel as getArtistContractStatusLabelBase,
  getContractStatusClassName,
} from '../../../../utils/contractStatus';

const HOLD_STATUS_LABELS = {
  pending: '回答待ち',
  conditional: '条件付き同意',
  approved: '参加可能',
  rejected: '参加不可',
  cancelled: 'キャンセル',
  cancelled_by_event_delete: '削除によるキャンセル',
  declined_by_admin: '不参加通知',
  expired: '期限切れ',
  no_response: '未回答',
  contract_sent: '本契約送付済み',
};

const HOLD_STATUS_CLASSES = {
  pending: 'status-pending',
  conditional: 'status-conditional',
  approved: 'status-approved',
  rejected: 'status-rejected',
  declined_by_admin: 'status-rejected',
  cancelled: 'status-cancelled',
  cancelled_by_event_delete: 'status-cancelled',
  expired: 'status-expired',
  no_response: 'status-expired',
  contract_sent: 'status-contract-sent',
};

const CONTRACT_STATUS_CLASSES = {
  billed: 'status-billed',
  billing_not_required: 'status-billed',
  pending: 'status-pending',
  counterproposal: 'status-counterproposal',
  cancelled: 'status-cancelled',
  cancelled_by_event_delete: 'status-cancelled',
  agreed: 'status-agreed',
  disagreed: 'status-disagreed',
  no_response: 'status-no-response',
  sent: 'status-contract-sent',
  contract_sent: 'status-contract-sent',
};

const resolveLabel = (map, status) => map[String(status || '').toLowerCase()] || status || 'ステータス未設定';
const resolveClass = (map, status) => map[String(status || '').toLowerCase()] || '';

export const getHoldStatusLabel = (status) => resolveLabel(HOLD_STATUS_LABELS, status);
export const getHoldStatusClass = (status) => resolveClass(HOLD_STATUS_CLASSES, status);
export const getAdminContractStatusLabel = (statusOrContract) =>
  getAdminContractStatusLabelBase(statusOrContract);
export const getArtistContractStatusLabel = (statusOrContract) =>
  getArtistContractStatusLabelBase(statusOrContract);
export const getContractStatusClass = (statusOrContract) =>
  getContractStatusClassName(statusOrContract) ||
  resolveClass(CONTRACT_STATUS_CLASSES, statusOrContract);
const getAdminContractDisplayStatusClass = (statusOrContract) =>
  getAdminContractDisplayStatusMetaBase(statusOrContract).className ||
  getContractStatusClass(statusOrContract);

export const getSummaryEntryTimestamp = (entry) => {
  if (entry?.created_at) {
    const t = new Date(entry.created_at).getTime();
    if (Number.isFinite(t)) return t;
  }
  if (entry?.date) {
    const t = new Date(entry.date).getTime();
    if (Number.isFinite(t)) return t;
  }
  return 0;
};

export const sortAndTrimSummaryEntries = (entries = [], limit = 4) =>
  (Array.isArray(entries) ? entries : [])
    .slice()
    .sort((a, b) => getSummaryEntryTimestamp(b) - getSummaryEntryTimestamp(a))
    .slice(0, limit);

const getHoldRequestTimestamp = (row) => {
  const value = row?.created_at || row?.updated_at || row?.event_date || '';
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const buildHoldRequestGroupKey = (row) => `${row?.hold_casting_id || ''}::${row?.artist_id || ''}`;

const normalizeHoldRequestEvent = (row) => ({
  event_uuid: row?.event_uuid,
  event_name: row?.event_name,
  name: row?.event_name,
  performance_type: row?.performance_type,
  event_mode: row?.event_mode,
  performances: row?.performances,
  event_date: row?.event_date,
  event_start_time: row?.event_start_time || row?.start_time,
  event_end_at: row?.event_end_at,
  flyer_front_url: row?.flyer_front_url,
  canceled_at: row?.canceled_at,
  cancel_reason: row?.cancel_reason,
  status: row?.status || row?.group_status || '',
  hold_casting_artist_id: row?.hold_casting_artist_id,
});

export const groupAdminHoldRequestRows = (rows = []) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = buildHoldRequestGroupKey(row);
    if (!key || key === '::') return;
    const entry = map.get(key) || {
      key,
      rows: [],
      events: [],
      isMultiple: false,
      isAllRequired: false,
    };
    entry.rows.push(row);
    entry.isMultiple = entry.isMultiple || !!row?.is_multiple_events;
    entry.isAllRequired = entry.isAllRequired || !!row?.is_all_events_required;
    const sourceEvents = Array.isArray(row?.events_all) && row.events_all.length
      ? row.events_all
      : [normalizeHoldRequestEvent(row)];
    sourceEvents.forEach((event) => {
      entry.events.push({
        ...normalizeHoldRequestEvent({ ...row, ...event }),
        ...event,
        status: event?.status || row?.status || row?.group_status || '',
        hold_casting_artist_id: event?.hold_casting_artist_id || row?.hold_casting_artist_id,
      });
    });
    map.set(key, entry);
  });

  const grouped = [];
  map.forEach((entry) => {
    const uniqueEvents = new Map();
    entry.events.forEach((event) => {
      const eventKey = event?.event_uuid || [
        event?.event_name || event?.name || '',
        event?.performance_type || '',
        event?.event_date || '',
      ].join('::');
      if (!uniqueEvents.has(eventKey)) uniqueEvents.set(eventKey, event);
    });
    const events = Array.from(uniqueEvents.values()).sort((a, b) => {
      const ad = String(a?.event_date || '');
      const bd = String(b?.event_date || '');
      if (ad !== bd) return ad.localeCompare(bd);
      const an = `${a?.event_name || a?.name || ''} ${a?.performance_type || ''}`.trim();
      const bn = `${b?.event_name || b?.name || ''} ${b?.performance_type || ''}`.trim();
      return an.localeCompare(bn, 'ja');
    });

    if (entry.isMultiple && entry.isAllRequired) {
      const base = entry.rows[0] || {};
      const latest = entry.rows.reduce((current, row) => (
        !current || getHoldRequestTimestamp(row) > getHoldRequestTimestamp(current) ? row : current
      ), null);
      const statusValue = base.group_status || base.status || '';
      grouped.push({
        ...base,
        id: `hold-${base.hold_casting_id || ''}-${base.artist_id || ''}`,
        created_at: latest?.created_at || base.created_at,
        group_status: statusValue,
        status: statusValue,
        events_all: events,
        __isGrouped: true,
        __groupKey: entry.key,
      });
      return;
    }

    entry.rows.forEach((row) => {
      grouped.push({
        ...row,
        __isGrouped: false,
        __groupKey: entry.key,
      });
    });
  });

  return grouped.sort((a, b) => getHoldRequestTimestamp(b) - getHoldRequestTimestamp(a));
};

export const extractAdminParticipantsFromMessages = (messages = [], { cutoffDays = 30 } = {}) => {
  const map = new Map();
  const cutoff = Date.now() - cutoffDays * 24 * 60 * 60 * 1000;
  (Array.isArray(messages) ? messages : [])
    .slice()
    .sort((a, b) => {
      const aTimestamp = new Date(a?.created_at || a?.createdAt).getTime();
      const bTimestamp = new Date(b?.created_at || b?.createdAt).getTime();
      return (Number.isFinite(bTimestamp) ? bTimestamp : 0) -
        (Number.isFinite(aTimestamp) ? aTimestamp : 0);
    })
    .forEach((message) => {
      if (message?.message_type !== 'admin_message') return;
      if (String(message.admin_status || '').toLowerCase() === 'suspended') return;
      if (message.admin_is_disabled || message.admin_is_suspended) return;
      const ts = new Date(message.created_at || message.createdAt).getTime();
      if (Number.isFinite(ts) && ts < cutoff) return;
      const cleanedName = String(
        message.admin_name ||
        message.admin_full_name ||
        message.admin_username ||
        message.admin_nickname ||
        ''
      ).trim();
      if (!cleanedName || cleanedName === '管理者') return;
      const key = message.admin_user_id || message.admin_email || cleanedName || message.admin_id;
      if (!key || map.has(key)) return;
      map.set(key, {
        id: key,
        fullName: cleanedName,
        nickname: message.admin_nickname || '',
        email: message.admin_email || '',
        position: message.admin_position || '',
        department: message.admin_department || '',
        picture: message.admin_profile_picture || '',
        comment: message.admin_profile_one_liner || message.admin_comment || message.admin_note || '',
        source: message,
      });
    });
  return Array.from(map.values());
};

export const mapAdminHoldSummaryEntry = (item) => {
  const primaryEvent = Array.isArray(item?.events_all) ? item.events_all[0] : null;
  const titleRaw = item?.event_name || primaryEvent?.event_name || 'イベント未設定';
  const performanceType = item?.performance_type || primaryEvent?.performance_type;
  const statusRaw = item?.group_status || item?.status;
  const eventUuid = item?.base_event_uuid || item?.event_uuid || primaryEvent?.event_uuid || null;
  const isGrouped = item?.__isGrouped || (item?.is_multiple_events && item?.is_all_events_required);
  const link = isGrouped && item?.hold_casting_id && item?.artist_id
    ? `/admin/workspaces/casting/offers/by-hold/${item.hold_casting_id}?artist_id=${item.artist_id}`
    : item?.hold_casting_artist_id
    ? `/admin/workspaces/casting/offers/${item.hold_casting_artist_id}`
    : (item?.hold_casting_id && item?.artist_id
      ? `/admin/workspaces/casting/offers/by-hold/${item.hold_casting_id}?artist_id=${item.artist_id}`
      : null);
  const eventCount = Array.isArray(item?.events_all) ? item.events_all.length : 0;
  const name = performanceType ? `${titleRaw} ${performanceType}` : titleRaw;

  return {
    id: item?.id || item?.hold_casting_artist_id || `hold-${titleRaw}-${item?.event_date || ''}-${statusRaw || ''}`,
    status: getHoldStatusLabel(statusRaw),
    statusClass: getHoldStatusClass(statusRaw),
    name: isGrouped && eventCount > 1 ? `${name} ほか${eventCount - 1}件` : name,
    date: item?.event_date || primaryEvent?.event_date || null,
    time: item?.event_start_time || primaryEvent?.event_start_time || primaryEvent?.start_time || null,
    created_at: item?.created_at || item?.updated_at || null,
    link: link || (eventUuid ? `/admin/workspaces/event-operations/events/${eventUuid}` : null),
  };
};

export const mapAdminContractSummaryEntry = (item) => {
  const primaryEvent = Array.isArray(item?.events) ? item.events[0] : null;
  const titleRaw = primaryEvent?.name || item?.contract_event_name || item?.event_name || 'イベント未設定';
  const performanceType = primaryEvent?.performance_type;
  const statusRaw = item?.status;
  const eventUuid = primaryEvent?.event_uuid || item?.contract_event_uuid || item?.event_uuid || null;

  return {
    id: item?.contract_artist_id || item?.id || `contract-${titleRaw}-${item?.contract_event_date || ''}-${statusRaw || ''}`,
    status: getAdminContractDisplayStatusLabelBase(item),
    statusClass: getAdminContractDisplayStatusClass(item),
    name: performanceType ? `${titleRaw} ${performanceType}` : titleRaw,
    date: primaryEvent?.event_date || item?.contract_event_date || null,
    time: primaryEvent?.event_start_time || primaryEvent?.start_time || item?.contract_event_start_time || null,
    created_at: item?.sent_at || item?.updated_at || null,
    link: item?.contract_artist_id
      ? `/admin/workspaces/contracts/${item.contract_artist_id}`
      : (eventUuid ? `/admin/workspaces/event-operations/events/${eventUuid}` : null),
  };
};

export const mapArtistOfferSummaryEntry = (item) => {
  const primaryEvent = Array.isArray(item?.events) ? item.events[0] : null;
  const titleRaw = primaryEvent?.event_name || item?.event_name || item?.subject || 'イベント未設定';
  const performanceType = primaryEvent?.performance_type;
  const statusRaw = primaryEvent?.status || item?.status;

  return {
    id: item?.id || `offer-${titleRaw}-${item?.event_date || ''}`,
    status: getHoldStatusLabel(statusRaw),
    statusClass: getHoldStatusClass(statusRaw),
    name: performanceType ? `${titleRaw} ${performanceType}` : titleRaw,
    date: primaryEvent?.event_date || item?.event_date || null,
    time: primaryEvent?.event_start_time || primaryEvent?.start_time || null,
    created_at: item?.created_at || null,
    link: item?.id ? `/artist/workspaces/casting/offers/${item.id}` : null,
  };
};

export const mapArtistContractSummaryEntry = (item) => {
  const primaryEvent = Array.isArray(item?.events) ? item.events[0] : null;
  const titleRaw = primaryEvent?.name || primaryEvent?.event_name || item?.contract_event_name || 'イベント未設定';
  const performanceType = primaryEvent?.performance_type;

  return {
    id: item?.contract_artist_id || item?.id || `contract-${titleRaw}-${primaryEvent?.event_date || ''}`,
    status: getArtistContractStatusLabel(item),
    statusClass: getContractStatusClass(item),
    name: performanceType ? `${titleRaw} ${performanceType}` : titleRaw,
    date: primaryEvent?.event_date || null,
    time: primaryEvent?.event_start_time || primaryEvent?.start_time || null,
    created_at: item?.sent_at || item?.updated_at || item?.created_at || null,
    link: item?.contract_artist_id ? `/artist/workspaces/contracts/${item.contract_artist_id}` : null,
  };
};
